/** The event page's query param that carries the formatted id of a ticket the viewer was just sent. */
export const RECEIVED_TICKET_PARAM = 'receivedTicket'

/** What the lookup reads from an owned ticket (`event.tickets` / the series parent's `tickets`). */
interface OwnedTicketLike {
	ticketId?: string | null
	eventTicket?: { ticketType?: string | null; price?: number | null } | null
}

interface ReceivedTicket {
	ticketName: string
	/** Paid tickets read as "ticket", free ones as "pass" (the rule the attendee lists use: `price ? 'tickets' : 'passes'`) */
	isPaid: boolean
}

interface ReceivedTicketCopy {
	title: string
	body: string
}

/**
 * The viewer's own ticket the param names, searching the page event's tickets then the series parent's (a season pass
 * lives there). Only a ticket the viewer actually holds is returned, so a made-up id finds nothing.
 */
export function findReceivedTicket(
	ticketId: string | null | undefined,
	...ticketLists: ReadonlyArray<ReadonlyArray<OwnedTicketLike | null | undefined> | null | undefined>
): ReceivedTicket | null {
	if (!ticketId) return null
	for (const tickets of ticketLists) {
		const ticket = tickets?.find(item => item?.ticketId === ticketId)
		if (ticket) {
			return { ticketName: ticket.eventTicket?.ticketType?.trim() ?? '', isPaid: !!ticket.eventTicket?.price }
		}
	}
	return null
}

/** The popup's copy, e.g. "You've been sent 1 pass (Series Pass) for The Leagues". */
export function buildReceivedTicketCopy({
	ticketName,
	isPaid,
	eventName,
}: ReceivedTicket & { eventName: string }): ReceivedTicketCopy {
	const noun = isPaid ? 'ticket' : 'pass'
	const named = ticketName ? ` (${ticketName})` : ''
	return {
		title: "You've received tickets",
		body: `You've been sent 1 ${noun}${named} for ${eventName}`,
	}
}

export type { ReceivedTicket, ReceivedTicketCopy }
